import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { FaSuitcase, FaUsers, FaGem } from 'react-icons/fa';
import { selectFleet, setDistance, setDuration, setMapsRecordId, setQuoteInfoRecordId, setFleetSelection, setFleetRecordWithRateId } from '../../features/bookingSlice/bookingSlice';
import loader from "../../assets/images/loader.gif";

const RightSideCars = ({ data }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const topRef = useRef(null);
  const bookingData = useSelector(state => state.booking);
  const { fleet_id } = useSelector(state => state.booking);

  useEffect(() => {
    if (bookingData.pickup?.coordinates && bookingData.dropoff?.coordinates && window.google) {
      const calculateAndSetRoute = async () => {
        const directionsService = new window.google.maps.DirectionsService();
        try {
          const result = await directionsService.route({
            origin: bookingData.pickup.coordinates,
            destination: bookingData.dropoff.coordinates,
            travelMode: window.google.maps.TravelMode.DRIVING,
            unitSystem: window.google.maps.UnitSystem.IMPERIAL,
            region: 'US'
          });

          if (result.status === 'OK' && result.routes[0]?.legs[0]) {
            const leg = result.routes[0].legs[0];
            dispatch(setDistance(leg.distance.text));
            dispatch(setDuration(leg.duration.text));
          } else {
            console.error('Directions request failed due to:', result.status);
          }
        } catch (error) {
          console.error('Error calculating directions:', error);
        }
      };

      calculateAndSetRoute();
    }
  }, [bookingData.pickup, bookingData.dropoff, dispatch]);

  const handleBookingRequest = async () => {
    console.log("Proceeding to next step...");
    if (selectedFleet) {
      dispatch(selectFleet(selectedFleet.fleet_id));
      dispatch(setFleetSelection(selectedFleet));
      if (selectedFleet.fleet_record_with_rate_id) {
        dispatch(setFleetRecordWithRateId(selectedFleet.fleet_record_with_rate_id));
      }
    }
    navigate("/vehicleextra");
  };

  useEffect(() => {
    if (data?.fleets?.length > 0 && !fleet_id) {
      const firstFleet = data.fleets[0];
      dispatch(selectFleet(firstFleet.fleet_id));
      dispatch(setFleetSelection(firstFleet));
      dispatch(setMapsRecordId(data.maps_record_id));
      dispatch(setQuoteInfoRecordId(data.quote_info_record_id));
    }
  }, [data, dispatch, fleet_id]);

  const selectVehicle = (fleet) => {
    dispatch(selectFleet(fleet.fleet_id));
    dispatch(setFleetSelection(fleet));
    if (fleet.fleet_record_with_rate_id) {
      dispatch(setFleetRecordWithRateId(fleet.fleet_record_with_rate_id));
    }
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  if (!data?.fleets) {
    return <div className="loading-container"><img src={loader} alt="Loading..." /></div>;
  }

  const selectedFleet = data.fleets.find((f) => f.fleet_id === fleet_id) || data.fleets[0];

  const getTotalFare = (fleet) => {
    if (!fleet || !fleet.fare_details) return '0.00';
    const fareDetails = fleet.fare_details;
    const total = parseFloat(fareDetails.final_fare || 0);
    return total > 0 ? total.toFixed(2) : '0.00';
  };

  const cardPrice = getTotalFare(selectedFleet);
  const cashPrice = selectedFleet ? (parseFloat(cardPrice) * 0.9).toFixed(2) : '0.00';

  return (
    <div className="vehicle-rates-container">
      {loading && <div className="full-page-loader"><img src={loader} alt="Loading..." /></div>}
      
      <div className="page-header" ref={topRef}>
        <h2>Vehicle & Rates</h2>
      </div>

      <div className="main-content-wrapper">
        <div className="left-column" style={{boxShadow: '0 0 10px 0 #00000080', borderRadius: '8px'}}>
          <div className="selected-vehicle-wrapper">

            <div className="selected-vehicle-card">
              <div className="details-section">
                <h4 style={{textAlign: 'center'}}>{selectedFleet.name}</h4>
                <p style={{textAlign: 'center'}}>{selectedFleet.info?.description}</p>
                <div className="image-section">
                  <img src={selectedFleet.info?.img_link || '/default-car.png'} alt={selectedFleet.name} />
                </div>
                <div className="payment-options">
                  <div className="payment-option">
                    <p className="payment-label">Pay by Card</p>
                    <p className="payment-price">${cardPrice}</p>
                  </div>
                  <div className="payment-option">
                    <p className="payment-label">Pay by Cash</p>
                    <p className="payment-price">${cashPrice}</p>
                    <p className="payment-discount">10% OFF</p>
                  </div>
                </div>
                <button className="book-now-btn" onClick={handleBookingRequest}>BOOK NOW</button>
              </div>
            </div>
          </div>
        </div>

        <div className="right-column">
          <div className="booking-details-card">
            <div className="details-header">
              <h4>Booking Details</h4>
              <button>Change</button>
            </div>
            <div className="details-body">
              <div className="detail-item"><span>Service:</span> <span>{bookingData.service_type === 'from_airport' ? 'From Airport' : bookingData.service_type === 'to_airport' ? 'To Airport' : 'N/A'}</span></div>
              <div className="detail-item"><span>Date:</span> <span>{bookingData.date || 'N/A'}</span></div>
              <div className="detail-item"><span>Time:</span> <span>{bookingData.time || 'N/A'}</span></div>
              <div className="detail-item"><span>Pick-Up:</span> <span>{bookingData.pickup?.address || 'N/A'}</span></div>
              <div className="detail-item"><span>Drop-Off:</span> <span>{bookingData.dropoff?.address || 'N/A'}</span></div>
              <div className="detail-item"><span>Passengers:</span> <span>{bookingData.passenger || '0'}</span></div>
              <div className="detail-item"><span>Luggage:</span> <span>{bookingData.luggage || '0'}</span></div>
              <div className="detail-item"><span>Booster:</span> <span>{bookingData.booster || '0'}</span></div>
              <div className="detail-item"><span>Regular:</span> <span>{bookingData.regular || '0'}</span></div>
              <div className="detail-item"><span>Infant:</span> <span>{bookingData.infant || '0'}</span></div>
            </div>
            <div className="suggested-vehicle">
              <h5>Vehicle (Suggested)</h5>
              <div className="vehicle-info-line"><span>Vehicle:</span> <span>{selectedFleet.name}</span></div>
              <div className="vehicle-info-line"><span>Total Payable:</span> <strong>${getTotalFare(selectedFleet)}</strong></div>
            </div>
          </div>
        </div>
      </div>

      <div className="more-car-options">
        <h4>More car Option</h4>
        <p>Select another vehicle option below that is more spacious and carries more luggage</p>
        <div className="options-grid">
          {data.fleets.map(fleet => (
            <div key={fleet.fleet_id} className={`option-card ${fleet.fleet_id === fleet_id ? 'selected' : ''}`} onClick={() => selectVehicle(fleet)}>
              <h5>{fleet.name}</h5>
              {fleet.info?.description && <p className="fleet-description" style={{ textAlign: 'center' }}>{fleet.info.description}</p>}
              <div className="option-content">
                <img src={fleet.info?.img_link || '/default-car.png'} alt={fleet.name} />
                <div className="option-fares">
                  <div className="total-payable"><span>Total Payable</span> <strong>${getTotalFare(fleet)}</strong></div>
                </div>
              </div>
              <div className="fleet-features">
                <div className="feature-box">
                  <FaSuitcase />
                  <span>{fleet.luggage} Luggage</span>
                </div>
                <div className="feature-box">
                  <FaUsers />
                  <span>{fleet.passengers} Passengers</span>
                </div>
                <div className="feature-box">
                  <FaGem />
                  <span>Premium</span>
                </div>
              </div>
              <button className="ride-btn">RIDE WITH THIS CAR</button>
            </div>
          ))}
        </div>
      </div>

      <style jsx>{`
        .vehicle-rates-container {
          width: 100%;
          max-width: 1200px;
          margin: 0 auto;
          padding: 20px;
          font-family: Arial, sans-serif;
          color: #333;
          background-color: #f0f2f5;
        }
        .page-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-bottom: 10px;
          margin-bottom: 20px;
        }
        .page-header h2 {
          font-size: 22px;
          font-weight: bold;
          color: #000;
        }
        .main-content-wrapper {
          display: flex;
          gap: 20px;
          margin-bottom: 30px;
        }
        .left-column {
          flex: 3;
        }
        .right-column {
          flex: 1.5;
        }
        .selected-vehicle-wrapper {
          position: relative;
        }
        .ssl-badge {
          position: absolute;
          top: -25px;
          right: -25px;
          width: 100px;
          height: auto;
          z-index: 10;
        }
        .selected-vehicle-card {
          display: flex;
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
          overflow: hidden;
        }
        .image-section {
          padding: 15px;
          text-align: center;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
        }
        .image-section img {
          max-width: 100%;
          height: auto;
          margin-bottom: 15px;
        }
        .details-section {
          flex: 1;
          padding: 20px;
          background: #fff;
          border-radius: 8px;
          box-shadow: 0 2px 10px rgba(0,0,0,0.1);
        }
        .details-section h4 {
          font-size: 18px;
          font-weight: bold;
          margin: 0 0 15px;
        }
        .booking-details-card {
          border: 1px solid #ccc;
          border-radius: 5px;
          background-color: #fff;
        }
        .details-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 10px 15px;
          background-color: #f0f2f5;
          border-bottom: 1px solid #ccc;
        }
        .details-header h4 {
          margin: 0;
          font-size: 16px;
          font-weight: bold;
        }
        .details-header button {
          font-size: 12px;
          cursor: pointer;
        }
        .details-body, .suggested-vehicle {
          padding: 15px;
        }
        .detail-item, .vehicle-info-line {
          display: flex;
          justify-content: space-between;
          font-size: 13px;
          padding: 5px 0;
        }
        .suggested-vehicle {
          border-top: 1px solid #ccc;
          background-color: #f0f2f5;
        }
        .suggested-vehicle h5 {
          font-size: 14px;
          font-weight: bold;
          margin: 0 0 10px;
        }
        .more-car-options {
          margin-top: 30px;
        }
        .more-car-options h4 {
          font-size: 20px;
          font-weight: bold;
        }
        .more-car-options p {
          font-size: 13px;
          color: #000000;
          margin-bottom: 20px;
        }
        .options-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(48%, 1fr));
          gap: 20px;
        }
        .option-card {
          border: 1px solid #ccc;
          border-radius: 5px;
          padding: 15px;
          cursor: pointer;
          background-color: #fff;
          display: flex;
          flex-direction: column;
        }
        .option-card.selected {
          border-color: #188038;
          box-shadow: 0 0 10px rgba(0,123,255,0.3);
        }
        .option-card h5 {
          font-size: 20px;
          font-weight: bold;
          margin: 0 0 10px;
          text-align: center;
        }
        .option-content {
          display: flex;
          gap: 15px;
          align-items: center;
          flex-grow: 1;
        }
        .option-content img {
          width: 150px;
          height: auto;
        }
        .option-fares {
          flex: 1;
          font-size: 13px;
        }
        .total-payable {
          font-weight: bold;
          margin-top: 8px;
          border-top: 1px solid #ccc;
          padding-top: 8px;
        }
        .book-now-btn {
          width: 100%;
          padding: 12px;
          font-size: 16px;
          font-weight: bold;
          background-color: #188038;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          margin-top: 15px;
          transition: background-color 0.3s ease;
        }
        .book-now-btn:hover {
          background-color: #126b2c;
        }

        .ride-btn {
          width: 100%;
          padding: 10px;
          margin-top: 15px;
          background-color: #188038;
          color: white;
          border: none;
          border-radius: 5px;
          cursor: pointer;
          font-weight: bold;
        }
        @media (max-width: 768px) {
          .main-content-wrapper {
            flex-direction: column;
          }
          .selected-vehicle-card {
            flex-direction: column;
          }
          .image-section {
            border-right: none;
            border-bottom: 1px solid #ccc;
          }
          .options-grid {
            grid-template-columns: 1fr;
          }
          .option-content {
            flex-direction: column;
            align-items: flex-start;
          }
          .option-content img {
            width: 100%;
            margin-bottom: 10px;
          }
        }
      `}</style>
    </div>
  );
};

export default RightSideCars;